/**
 * Magento helper.
 *
 * Single home for Magento-only logic:
 *   1. Types - Magento API response/payment shapes
 *   2. API calls - requests to the `create-magento-order` Edge Function
 *   3. Page helpers - pure functions used by OrderPage / PaymentPage
 */
import { getSupabaseUrl } from "./supabase-api";
import type { MarketplaceRetailer } from "./supabase-api";
import type { AmastyStoreLocation } from "./amasty-api";
import type { CartItem, MarketplaceOrderShipping, ShippingInfo } from "@/types";
import { getSessionToken } from "./utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MagentoShippingTotals {
  grand_total: number | string;
  subtotal?: number | string;
  shipping_amount?: number | string;
  discount_amount?: number | string;
  tax_amount?: number | string;
  subtotal_with_discount?: number | string;
  shipping_discount_amount?: number | string;
}

export interface MagentoSetShippingResponse {
  success: boolean;
  data: {
    shipping_response?: {
      totals?: MagentoShippingTotals;
    };
  };
}

export interface MagentoCartMutationResponse {
  updated?: boolean;
  removed?: boolean;
}

export interface MagentoShippingMethod {
  carrier_code: string;
  method_code: string;
  carrier_title: string;
  method_title: string;
  price_incl_tax: number;
}

export interface MagentoPaymentTotals {
  subtotalCents: number;
  shippingCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  retailerTotals?: Record<string, Omit<MagentoPaymentTotals, "retailerTotals">>;
}

export interface MagentoVerifyTotalItem {
  id: number | string;
  price: number | string;
  qty: number;
  discountAmount: number | string;
  discountPerc: number | string;
}

export interface MagentoVerifyTotalResponse {
  total: number | string;
  shippingTotal: number | string;
  items: MagentoVerifyTotalItem[];
  tax: number | string;
  discount: number | string;
}

export interface MagentoVerifiedCartReconciliation {
  subtotalCents: number;
  shippingCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  itemPricesCents: Map<number, number>;
  itemDiscountsCents: Map<number, number>;
}

/** Reconciles a mismatched Magento cart response against its local cart rows. */
export function reconcileMagentoVerifiedCart(
  response: MagentoVerifyTotalResponse,
  cartItems: CartItem[],
): MagentoVerifiedCartReconciliation {
  const amountInCents = (amount: number | string, field: string): number => {
    const numericAmount = typeof amount === "string" ? Number(amount) : amount;
    if (!Number.isFinite(numericAmount)) throw new Error(`Magento returned an invalid ${field}.`);
    return Math.round(numericAmount * 100);
  };
  const localItemsById = new Map<number, CartItem>();
  for (const item of cartItems) {
    const id = Number(item.external_item_id);
    if (!Number.isSafeInteger(id) || id <= 0 || localItemsById.has(id)) {
      throw new Error(`Could not match Magento item IDs for ${item.title}. Please return to review.`);
    }
    localItemsById.set(id, item);
  }

  const itemPricesCents = new Map<number, number>();
  const itemDiscountsCents = new Map<number, number>();
  const responseIds = new Set<number>();
  let subtotalCents = 0;
  for (const verifiedItem of response.items ?? []) {
    const itemId = Number(verifiedItem.id);
    if (!Number.isSafeInteger(itemId) || responseIds.has(itemId)) {
      throw new Error("Magento returned invalid or duplicate cart item IDs.");
    }
    responseIds.add(itemId);
    const localItem = localItemsById.get(itemId);
    if (!localItem || localItem.quantity !== verifiedItem.qty) {
      throw new Error("Magento cart items changed. Please return to review your cart.");
    }
    const priceCents = amountInCents(verifiedItem.price, "item price");
    const discountAmountCents = Math.abs(amountInCents(verifiedItem.discountAmount ?? 0, "item discount"));
    const discountPercent = Number(verifiedItem.discountPerc ?? 0);
    if (priceCents < 0 || discountAmountCents < 0 || !Number.isFinite(discountPercent) || discountPercent < 0) {
      throw new Error("Magento returned invalid item pricing. Please return to review.");
    }
    const itemSubtotalCents = priceCents * verifiedItem.qty;
    // Item-specific discounts describe allocations of the cart-level discount,
    // so they are deliberately not deducted on top of response.discount.
    const itemDiscountCents = discountAmountCents > 0
      ? discountAmountCents
      : Math.round(itemSubtotalCents * discountPercent / 100);
    if (itemDiscountCents > itemSubtotalCents) {
      throw new Error("Magento returned an invalid item discount.");
    }
    itemPricesCents.set(itemId, priceCents);
    itemDiscountsCents.set(itemId, itemDiscountCents);
    subtotalCents += itemSubtotalCents;
  }
  if (localItemsById.size !== itemPricesCents.size) {
    throw new Error("Magento did not return pricing for every cart item. Please return to review.");
  }

  const shippingCents = amountInCents(response.shippingTotal, "shipping total");
  const discountCents = Math.abs(amountInCents(response.discount, "discount total"));
  const taxCents = amountInCents(response.tax ?? 0, "tax");
  const totalCents = amountInCents(response.total, "grand total");
  if (shippingCents < 0 || discountCents < 0 || taxCents < 0 || totalCents <= 0) {
    throw new Error("Magento returned invalid cart totals. Please return to review.");
  }
  const exclusiveTaxTotal = subtotalCents + shippingCents - discountCents;
  const taxInclusiveTotal = exclusiveTaxTotal + taxCents;
  let reconciledTaxCents = 0;
  if (exclusiveTaxTotal !== totalCents) {
    if (taxInclusiveTotal !== totalCents) {
      throw new Error("Magento's item prices, shipping, discounts, and tax do not add up to its total.");
    }
    reconciledTaxCents = taxCents;
  }
  return {
    subtotalCents,
    shippingCents,
    discountCents,
    taxCents: reconciledTaxCents,
    totalCents,
    itemPricesCents,
    itemDiscountsCents,
  };
}

export interface CreateMagentoOrderRequest {
  cart_id: string;
  retailer_id: string;
  email: string;
  customer_id: string;
  nile_order_id: string;
}

export interface CreateMagentoOrderResponse {
  success: boolean;
  data: {
    order_id: string;
  };
}

export interface MagentoCartRegistry {
  cartIds: Map<string, string>;
  conflictingBrands: Set<string>;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

const getHeaders = (sessionToken = getSessionToken()) => ({
  "x-api-key": import.meta.env.VITE_SUPABASE_ANON_KEY || "",
  "x-session-token": sessionToken ?? "",
  "Content-Type": "application/json",
});

interface MagentoEndpointResponse<T> {
  success: boolean;
  data?: T;
  error?: { message?: string };
}

function shopperMagentoError(message: string): string {
  const jsonStart = message.indexOf("{");
  if (jsonStart >= 0) {
    try {
      const parsed: unknown = JSON.parse(message.slice(jsonStart));
      if (parsed && typeof parsed === "object" && "message" in parsed) {
        const nested = (parsed as { message?: unknown }).message;
        if (typeof nested === "string" && nested.trim()) return nested;
      }
    } catch {
      // Keep the endpoint's original message when it doesn't contain valid JSON.
    }
  }
  return message;
}

async function postMagentoEndpoint<T>(
  endpoint: string,
  body: Record<string, unknown>,
  sessionToken = getSessionToken() ?? "",
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(
    `${getSupabaseUrl()}/functions/v1/create-magento-order/${endpoint}`,
    {
      method: "POST",
      headers: getHeaders(sessionToken),
      body: JSON.stringify(body),
      signal,
    },
  );
  let payload: MagentoEndpointResponse<T>;
  try {
    payload = await response.json() as MagentoEndpointResponse<T>;
  } catch (error) {
    if (["create-cart", "add-item", "update-cart-item", "remove-cart-item"].includes(endpoint)) {
      console.info("[Magento cart] response", {
        endpoint,
        status: response.status,
        ok: response.ok,
        parseError: error instanceof Error ? error.message : "Invalid JSON response",
      });
    }
    throw error;
  }
  if (["create-cart", "add-item", "update-cart-item", "remove-cart-item"].includes(endpoint)) {
    const data = payload.data as {
      cart_id?: unknown;
      item?: { item_id?: unknown; price?: unknown; price_incl_tax?: unknown };
    } | undefined;
    console.info("[Magento cart] response", {
      endpoint,
      status: response.status,
      ok: response.ok,
      success: payload.success,
      ...(endpoint === "create-cart" ? { hasCartId: Boolean(data?.cart_id) } : {}),
      ...(data?.item?.item_id !== undefined ? { itemId: data.item.item_id } : {}),
      ...(data?.item?.price !== undefined || data?.item?.price_incl_tax !== undefined
        ? { price: data.item.price ?? data.item.price_incl_tax }
        : {}),
      ...(payload.error?.message ? { error: payload.error.message } : {}),
    });
  }
  if (!response.ok || !payload.success || payload.data === undefined) {
    throw new Error(shopperMagentoError(payload.error?.message || `Magento ${endpoint} failed`));
  }
  return payload.data;
}

/** Creates a Magento guest cart for a retailer using this shopper's session. */
export async function createMagentoCart(
  retailerId: string,
  sessionToken: string,
): Promise<string> {
  const data = await postMagentoEndpoint<{ cart_id: string }>(
    "create-cart",
    { retailer_id: retailerId },
    sessionToken,
  );
  if (!data.cart_id) throw new Error("Magento did not return a cart ID");
  return data.cart_id;
}

/** Adds one base product SKU with an optional selected configurable variant SKU. */
export async function addMagentoCartItem(
  input: {
    retailerId: string;
    cartId: string;
    sku: string;
    quantity: number;
    selectedVariant: string | null;
  },
  sessionToken: string,
): Promise<{ item_id: number; price?: number }> {
  const data = await postMagentoEndpoint<{
    item?: { item_id?: number; price?: number; price_incl_tax?: number };
  }>(
    "add-item",
    {
      retailer_id: input.retailerId,
      cart_id: input.cartId,
      sku: input.sku,
      quantity: input.quantity,
      selected_variant: input.selectedVariant,
    },
    sessionToken,
  );
  if (typeof data.item?.item_id !== "number") {
    throw new Error("Magento did not return a cart item ID");
  }
  return {
    item_id: data.item.item_id,
    price: data.item.price ?? data.item.price_incl_tax,
  };
}

export async function updateMagentoCartItem(
  retailerId: string,
  cartId: string,
  itemId: number,
  quantity: number,
  sessionToken: string,
): Promise<void> {
  await postMagentoEndpoint("update-cart-item", {
    retailer_id: retailerId,
    cart_id: cartId,
    item_id: itemId,
    qty: quantity,
  }, sessionToken);
}

export async function removeMagentoCartItem(
  retailerId: string,
  cartId: string,
  itemId: number,
  sessionToken: string,
): Promise<void> {
  await postMagentoEndpoint("remove-cart-item", {
    retailer_id: retailerId,
    cart_id: cartId,
    item_id: itemId,
  }, sessionToken);
}

export async function createMagentoOrder(
  request: CreateMagentoOrderRequest,
): Promise<CreateMagentoOrderResponse> {
  const data = await postMagentoEndpoint<CreateMagentoOrderResponse["data"]>(
    "create-order",
    { ...request },
  );
  return { success: true, data };
}

export async function fetchMagentoShipping(
  retailerId: string,
  cartId: string,
  shippingAddress: object,
): Promise<MagentoShippingMethod[]> {
  if (!retailerId || !cartId) {
    throw new Error("Retailer ID and cart ID are required to fetch shipping estimates");
  }
  const data = await postMagentoEndpoint<{ shipping_options?: MagentoShippingMethod[] }>(
    "shipping-estimate",
    {
      retailer_id: retailerId,
      cart_id: cartId,
      shipping_address: shippingAddress,
    },
  );
  return data.shipping_options ?? [];
}

export async function removeCartItem(
  retailerId: string,
  cartId: string,
  itemId: number,
): Promise<unknown> {
  return postMagentoEndpoint("remove-cart-item", {
    retailer_id: retailerId,
    cart_id: cartId,
    item_id: itemId,
  });
}

export async function updateCartItem(
  retailerId: string,
  cartId: string,
  itemId: number,
  qty: number,
): Promise<MagentoCartMutationResponse> {
  return postMagentoEndpoint<MagentoCartMutationResponse>("update-cart-item", {
    retailer_id: retailerId,
    cart_id: cartId,
    item_id: itemId,
    qty,
  });
}

export async function setShipping(
  cartId: string,
  retailerId: string,
  address: Record<string, unknown> & { email?: string },
  carrierCode: string,
  methodCode: string,
  amastyStore?: string,
): Promise<MagentoSetShippingResponse> {
  const data = await postMagentoEndpoint<MagentoSetShippingResponse["data"]>(
    "add-shipping",
    {
      cart_id: cartId,
      retailer_id: retailerId,
      shipping_address: address,
      email: address.email,
      carrier_code: carrierCode,
      method_code: methodCode,
      amasty_store: amastyStore ?? null,
    },
  );
  return { success: true, data };
}

/** Fetches Magento's current cart total breakdown for diagnostics. */
export async function verifyMagentoTotal(
  retailerId: string,
  cartId: string,
): Promise<MagentoVerifyTotalResponse> {
  return postMagentoEndpoint<MagentoVerifyTotalResponse>("verify-total", {
    retailer_id: retailerId,
    cart_id: cartId,
  });
}

export async function fetchStoresAmasty(
  retailerId: string,
  filter: string,
  pageSize: number,
  currentPage: number,
): Promise<{ stores: AmastyStoreLocation[]; totalNearbyStores: number; totalPages: number }> {
  return postMagentoEndpoint("fetch-amasty-stores", {
    retailer_id: retailerId,
    filter,
    page_size: pageSize,
    current_page: currentPage,
  });
}

/**
 * Per-product verification result from the Magento verify-products endpoint.
 * `stock` is the available quantity for the SKU.
 */
export interface MagentoProductVerification {
  sku: string;
  stock: number;
}

/**
 * Verify Magento product SKUs and retrieve their current stock quantity.
 * Endpoint: POST /functions/v1/create-magento-order/verify-products
 */
export async function verifyMagentoProducts(
  retailerId: string,
  skus: string[],
  signal?: AbortSignal,
  sessionToken?: string,
): Promise<MagentoProductVerification[]> {
  const data = await postMagentoEndpoint<
    { results?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>
  >(
    "verify-products",
    { retailer_id: retailerId, skus },
    sessionToken,
    signal,
  );
  const results = Array.isArray(data) ? data : (data.results ?? []);
  return results.flatMap((entry) =>
    Object.entries(entry ?? {}).map(([sku, value]) => ({
      sku,
      stock: Math.max(0, Math.floor(Number(value) || 0)),
    })),
  );
}

// ---------------------------------------------------------------------------
// Page helpers
// ---------------------------------------------------------------------------

function randsToCents(value: number | string | undefined, field: string): number {
  const amount = typeof value === "string" ? Number(value) : value;
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    throw new Error(`Magento returned an invalid ${field}. Please try again.`);
  }
  return Math.round(amount * 100);
}

export function paymentTotalsFromMagento(
  totals: MagentoShippingTotals | undefined,
  fallbackSubtotalCents: number,
): MagentoPaymentTotals {
  if (!totals) {
    throw new Error("Magento did not return payment totals. Please try again.");
  }

  const subtotalCents =
    totals.subtotal === undefined
      ? fallbackSubtotalCents
      : randsToCents(totals.subtotal, "subtotal");
  const shippingCents = randsToCents(totals.shipping_amount, "shipping total");
  const discountCents = Math.abs(
    randsToCents(totals.discount_amount, "discount total"),
  );
  const taxCents = randsToCents(totals.tax_amount, "tax total");
  const totalCents = randsToCents(totals.grand_total, "grand total");

  if (
    subtotalCents < 0 ||
    shippingCents < 0 ||
    discountCents < 0 ||
    taxCents < 0 ||
    totalCents <= 0
  ) {
    throw new Error("Magento returned invalid payment totals. Please try again.");
  }

  return { subtotalCents, shippingCents, discountCents, taxCents, totalCents };
}

/**
 * Store fields needed to confirm Magento shipping and totals.
 * Structurally compatible with the OrderPage/PaymentPage order-details store.
 */
export interface MagentoShippingContext {
  isMarketplace: boolean;
  apiConfig: { type: string } | null;
  magentoCartIds: Record<string, string>;
  themeId: string | null;
  selectedShippingOption: { carrier_code?: string; method_code?: string } | null;
  selectedPickupLocationId: string | null;
  shipments: MarketplaceOrderShipping[];
  marketplaceRetailers: MarketplaceRetailer[];
  cartItemsByBrand: [string, CartItem[]][];
  totalDiscountDetail: { brandId?: string; totalValue: number }[];
}

/**
 * Whether this checkout needs Magento shipping confirmation:
 * a single Magento brand, or a marketplace cart with at least one Magento shipment.
 * Callers use this to decide before calling applyMagentoShippingTotals.
 */
export function isMagentoCheckout(store: MagentoShippingContext): boolean {
  if (!store.isMarketplace) {
    return store.apiConfig?.type === "magento";
  }
  return store.shipments.some((shipment) =>
    store.marketplaceRetailers.some(
      (retailer) =>
        retailer.retailer_id === shipment.child_retailer_id &&
        retailer.integration_type === "magento",
    ),
  );
}

/**
 * Confirm shipping on the Magento cart and derive the authoritative payment
 * totals. Returns null only for a marketplace cart with no Magento shipments.
 *
 * Prerequisite: the caller has already checked the integration type
 * (see isMagentoCheckout). This function only handles Magento.
 */
export async function applyMagentoShippingTotals(
  store: MagentoShippingContext,
  shippingDetails: ShippingInfo,
  calculated: {
    subtotalCents: number;
    shippingCents: number;
    discountCents: number;
  },
): Promise<MagentoPaymentTotals | null> {
  const address = shippingDetails as unknown as Record<string, unknown> & {
    email?: string;
  };

  // Single-brand Magento cart
  if (!store.isMarketplace) {
    const magentoCartId = store.themeId
      ? store.magentoCartIds[store.themeId]
      : undefined;
    if (
      !magentoCartId ||
      !store.themeId ||
      !store.selectedShippingOption?.carrier_code ||
      !store.selectedShippingOption.method_code
    ) {
      throw new Error("Magento shipping details are incomplete. Please return to review.");
    }

    const response = await setShipping(
      magentoCartId,
      store.themeId,
      address,
      store.selectedShippingOption.carrier_code,
      store.selectedShippingOption.method_code,
      store.selectedPickupLocationId ?? undefined,
    );
    const totals = paymentTotalsFromMagento(
      response.data.shipping_response?.totals,
      calculated.subtotalCents,
    );
    return { ...totals, retailerTotals: { [store.themeId]: totals } };
  }

  // Marketplace cart: reconcile totals per Magento shipment
  let reconciledShippingCents = calculated.shippingCents;
  let reconciledDiscountCents = calculated.discountCents;
  let reconciledTaxCents = 0;
  let hasMagentoShipment = false;
  const retailerTotals: Record<string, Omit<MagentoPaymentTotals, "retailerTotals">> = {};

  for (const shipment of store.shipments) {
    const retailer = store.marketplaceRetailers.find(
      (candidate) => candidate.retailer_id === shipment.child_retailer_id,
    );
    if (retailer?.integration_type !== "magento") continue;
    const brandCartId = store.magentoCartIds[shipment.child_retailer_id];
    if (!brandCartId || !shipment.carrier_code || !shipment.method_code) {
      throw new Error(`Magento shipping details are incomplete for ${retailer.name}.`);
    }

    const response = await setShipping(
      brandCartId,
      shipment.child_retailer_id,
      address,
      shipment.carrier_code,
      shipment.method_code,
      shipment.store_pickup,
    );
    const brandSubtotalCents = (store.cartItemsByBrand.find(
      ([brandId]) => brandId === shipment.child_retailer_id,
    )?.[1] ?? []).reduce((sum, item) => sum + item.line_total_cents, 0);
    const totals = paymentTotalsFromMagento(
      response.data.shipping_response?.totals,
      brandSubtotalCents,
    );
    retailerTotals[shipment.child_retailer_id] = totals;
    const previousBrandDiscountCents = store.totalDiscountDetail
      .filter((discount) => discount.brandId === shipment.child_retailer_id)
      .reduce((sum, discount) => sum + discount.totalValue, 0);

    reconciledShippingCents += totals.shippingCents - shipment.shipping_price_cents;
    reconciledDiscountCents += totals.discountCents - previousBrandDiscountCents;
    reconciledTaxCents += totals.taxCents;
    hasMagentoShipment = true;
  }

  if (!hasMagentoShipment) return null;

  const reconciledTotalCents =
    calculated.subtotalCents +
    reconciledShippingCents -
    reconciledDiscountCents +
    reconciledTaxCents;
  if (!Number.isSafeInteger(reconciledTotalCents) || reconciledTotalCents <= 0) {
    throw new Error("Magento returned invalid marketplace payment totals. Please try again.");
  }

  return {
    subtotalCents: calculated.subtotalCents,
    shippingCents: reconciledShippingCents,
    discountCents: reconciledDiscountCents,
    taxCents: reconciledTaxCents,
    totalCents: reconciledTotalCents,
    retailerTotals,
  };
}

/** Builds the mounted cart's Magento cart lookup from its initial rows once. */
export function seedMagentoCartRegistry(items: CartItem[]): MagentoCartRegistry {
  const cartIds = new Map<string, string>();
  const conflictingBrands = new Set<string>();
  for (const item of items) {
    if (!item.fulfillment_brand_id || !item.brand_cart_id) continue;
    const existing = cartIds.get(item.fulfillment_brand_id);
    if (existing && existing !== item.brand_cart_id) {
      conflictingBrands.add(item.fulfillment_brand_id);
    } else {
      cartIds.set(item.fulfillment_brand_id, item.brand_cart_id);
    }
  }
  return { cartIds, conflictingBrands };
}
